import type { InfiniteData, QueryKey } from '@tanstack/react-query'

import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Action,
  Event,
  MutationCancelEventRegistrationArgs,
} from '@tribeplatform/gql-client/types'
import { ActionFields } from '@tribeplatform/gql-client/types/generated'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'

import { getEventKey, getEventRecurrencesKey } from './event.key.js'
import { getQueriesContainingEvent } from './utils.js'

export const useCancelEventRegistration = (options?: {
  fields?: ActionFields
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationCancelEventRegistrationArgs,
    { snapshot: [QueryKey, InfiniteData<Event>][] }
  >
}) => {
  const { useMutationOptions, fields } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<
    Action,
    ClientError,
    MutationCancelEventRegistrationArgs,
    { snapshot: [QueryKey, InfiniteData<Event>][] }
  >(
    (input: MutationCancelEventRegistrationArgs) =>
      client.events.cancelEventRegistration(input, fields),
    {
      ...useMutationOptions,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(
          getEventKey({ variables: { id: variables.eventId } }),
        )
        const event = queryClient.getQueryData<Event>(
          getEventKey({ variables: { id: variables.eventId } }),
        )

        queryClient.invalidateQueries(
          getEventRecurrencesKey({
            variables: { recurrenceId: event?.recurrenceId },
          }),
        )

        getQueriesContainingEvent(queryClient, variables.eventId).forEach(
          query => queryClient.invalidateQueries(query.queryKey),
        )
        useMutationOptions?.onSuccess?.(data, variables, context)
      },
    },
  )
}
